import { Box, Stack, Theme, useTheme } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import ShortcutTab from './shortcut-tab';
import HistoryTab from './history-tab';
import useThread from '@/hooks/use-thread.hook';
import useApp from '@/hooks/use-app.hook';
import MobileUserFooter from './mobile-user-footer';
import UserSettingsTab from './user-settings-tab';
import AgentTab from './agent-tab';
import useFeature from '@/hooks/use-feature.hook';
import Icon from '../icon';
import MobileMenuItem from './mobile-menu-item';
import RmDrawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { isChatPath, TAB_ID, TabConfig } from './helpers';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { openNewTab } from '@/lib/url-helpers';
import { getAdminBaseUrl } from '@/lib/services/auth.service';
import SurepathIcon from '../icon/surepath-icon';
import { pathIsActive } from '@/lib/helpers';
import Button from '../button';
import PreferencesTab from './preferences-tab';

const MOBILE_BAR_WIDTH = 292;

const useStyles = createUseStyles<string, { width: number; open: boolean }>((theme: Theme) => ({
  drawer: {
    position: 'relative',
    maxWidth: 'calc(100vw - 45px)',
  },
  menuButton: {
    color: theme.palette.grey[800],
  },
  closeButton: ({ open }) => ({
    position: 'absolute',
    top: 5,
    right: open ? -40 : 100000,
    zIndex: theme.zIndex.drawer + 10,
    padding: 4,
    height: 26,
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    border: `1px solid ${theme.palette.common.white}`,
    borderRadius: '50%',
    cursor: 'pointer',
    opacity: 0.6,
  }),
  mainBar: {
    backgroundColor: theme.palette.background.sidebar,
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  mainBarInner: ({ open }) => ({
    flexGrow: 1,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    padding: '8px 0px 0px 0px',
    opacity: open ? 1 : 0,
    transition: theme.transitions.create('opacity', {
      delay: 200,
      easing: theme.transitions.easing.sharp,
      duration: 10,
    }),
  }),
}));

const MobileSidebar: FC = () => {
  const { loadThread } = useThread();
  const { signedIn } = useApp();
  const { canViewHistory, canViewAdminUI, canUseChat } = useFeature();
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const navigate = useNavigate();

  const theme = useTheme();

  const styles = useStyles({
    width: MOBILE_BAR_WIDTH,
    open,
  });

  const [activeTab, setActiveTab] = useState<TAB_ID>('none');

  const handleToggleOpen = () => {
    setOpen((old) => !old);
  };

  const handleNewThread = () => {
    loadThread();
    setOpen(false);
  };

  const handleSwitchTab = (tabId: TAB_ID) => {
    setOpen(true);
    setActiveTab(tabId);
  };

  const handleClickTab = (tabConfig: TabConfig) => {
    const { id: tabId, action, href } = tabConfig;

    switch (action) {
      case 'page':
        if (!isChatPath(href!)) {
          setOpen(false);
        }
        setActiveTab(tabId);
        navigate({ to: href });
        break;
      case 'link':
        setOpen(false);
        openNewTab(href!);
        break;
      case 'tab':
        handleSwitchTab(tabId);
        break;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLoadAgent = () => {
    setActiveTab('none');
    setOpen(false);
    navigate({ to: '/chat' });
  };

  const isChat = isChatPath(router.state.location.pathname);

  const menuItems: TabConfig[] = useMemo(() => {
    const menuItems: TabConfig[] = [
      {
        id: 'none',
        label: 'Apps',
        action: 'page',
        icon: 'app-switcher',
        href: '/apps',
        primary: true,
        childHrefs: ['/catalog'],
      },
      { action: 'page', icon: 'catalog', label: 'Catalog', id: 'catalog', href: '/catalog' },
      {
        action: 'link',
        image: <SurepathIcon />,
        label: 'Admin',
        id: 'admin-ui',
        href: getAdminBaseUrl(),
        disabled: !canViewAdminUI,
      },
      {
        action: 'tab',
        icon: 'history',
        label: 'Chat History',
        id: 'history',
        disabled: !canViewHistory,
      },
      {
        action: 'tab',
        icon: 'assistant',
        label: 'Assistants',
        id: 'agents',
        disabled: !canUseChat,
      },
      {
        action: 'tab',
        icon: 'shortcut',
        label: 'Shortcuts',
        id: 'shortcuts',
        disabled: !canUseChat,
      },
      {
        action: 'tab',
        icon: 'settings',
        label: 'Settings',
        id: 'user-settings',
        disabled: !canUseChat,
      },
      { action: 'tab', icon: 'display-settings', label: 'Preferences', id: 'preferences' },
    ];

    return menuItems;
  }, [canViewAdminUI, canViewHistory, canUseChat]);

  return (
    <>
      <Icon name="menu" onClick={handleToggleOpen} className={styles.menuButton} />

      <RmDrawer
        direction="left"
        open={open}
        overlayOpacity={0}
        duration={200}
        size={MOBILE_BAR_WIDTH}
        zIndex={theme.zIndex.drawer}
        onClose={handleToggleOpen}
        className={styles.drawer}
      >
        <Box className={styles.closeButton} onClick={handleToggleOpen} role="button">
          <Icon name="chevron-left" size="small" />
        </Box>

        <Stack justifyContent="flex-start" alignItems="flex-start" className={styles.mainBar}>
          <Stack width="100%" p={1}>
            {menuItems.map((tabConfig) => {
              const { id, icon, image, label, disabled, href, action } = tabConfig;

              if (disabled) {
                return null;
              }

              const isActive =
                action === 'tab' ? activeTab === id : !activeTab && !!href && pathIsActive(href);

              return (
                <MobileMenuItem
                  key={id}
                  icon={icon}
                  image={image}
                  label={label}
                  onClick={() => handleClickTab(tabConfig)}
                  active={isActive}
                />
              );
            })}
          </Stack>

          <Stack className={styles.mainBarInner}>
            <Box flexGrow={1}>
              {activeTab === 'agents' && <AgentTab onLoadAgent={handleLoadAgent} />}
              {activeTab === 'shortcuts' && <ShortcutTab onLoadShortcut={handleClose} />}
              {activeTab === 'history' && <HistoryTab onLoadThread={handleClose} />}
              {activeTab === 'user-settings' && <UserSettingsTab />}
              {activeTab === 'preferences' && <PreferencesTab onClose={handleClose} />}
            </Box>
            {signedIn && isChat && (
              <Box p={1} width="100%">
                <Button
                  icon="plus"
                  label="New Chat"
                  onClick={handleNewThread}
                  size="small"
                  fullWidth
                />
              </Box>
            )}
            {signedIn && <MobileUserFooter />}
          </Stack>
        </Stack>
      </RmDrawer>
    </>
  );
};

export default MobileSidebar;
